import { useState } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../../api/endpoints.js';
import { Page } from '../../components/AppShell.jsx';
import {
  Badge, Button, Card, DataTable, Dialog, Field, Input, Pagination, Select, Textarea,
} from '../../components/ui.jsx';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '../../lib/toast.js';
import { useListQuery } from '../../hooks/useListQuery.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { PERMISSIONS } from '../../lib/permissions.js';
import { dateNumeric, initials } from '../../lib/format.js';
import AgentIdCard from './AgentIdCard.jsx';

/**
 * The agent roster.
 *
 * An agent is a back-office record, not an account: creating one grants no
 * access to anything. Creating happens on its own page, where the ID card can
 * preview beside the form.
 */
export default function AgentsPage() {
  const { can } = useAuth();

  const list = useListQuery({
    key: 'admin-agents',
    fetcher: api.agents.list,
    initialFilters: { status: '' },
    searchable: true,
  });

  const queryClient = useQueryClient();

  const [idCardAgent, setIdCardAgent] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [reason, setReason] = useState('');
  const [isWorking, setIsWorking] = useState(false);

  const closeDelete = () => {
    setDeleting(null);
    setReason('');
  };

  const remove = async () => {
    setIsWorking(true);

    try {
      await api.agents.remove(deleting._id, reason.trim());
      toast.success(`${deleting.name} has been removed from the roster.`);
      closeDelete();
      queryClient.invalidateQueries({ queryKey: ['admin-agents'] });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsWorking(false);
    }
  };

  const columns = [
    {
      key: 'name',
      header: 'Agent',
      render: (agent) => (
        <div className="row">
          <div
            className="sidebar__avatar"
            style={{ background: 'var(--surface-container)', color: 'var(--primary)' }}
          >
            {initials(agent.name)}
          </div>
          <div>
            <div className="table__primary">{agent.name}</div>
            <div className="table__muted">{agent.email}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'employeeId',
      header: 'Employee ID',
      render: (agent) => <span className="mono">{agent.employeeId}</span>,
    },
    {
      key: 'role',
      header: 'Role',
      render: (agent) => (
        <div>
          <div>{agent.designation}</div>
          <div className="table__muted">{agent.department}</div>
        </div>
      ),
    },
    {
      key: 'phone',
      header: 'Phone',
      render: (agent) => <span className="mono">{agent.phone}</span>,
    },
    {
      key: 'joined',
      header: 'Joined',
      render: (agent) => <span className="table__muted">{dateNumeric(agent.joiningDate)}</span>,
    },
    { key: 'status', header: 'Status', render: (agent) => <Badge status={agent.status} /> },
    {
      key: 'actions',
      header: '',
      className: 'table__actions',
      render: (agent) => (
        <div className="row">
          {can(PERMISSIONS.AGENT_MANAGE) ? (
            <Link to={`/agents/${agent._id}/edit`}>
              <Button size="sm" variant="ghost">
                Edit
              </Button>
            </Link>
          ) : null}
          <Button size="sm" variant="outline" onClick={() => setIdCardAgent(agent)}>
            ID card
          </Button>
          {can(PERMISSIONS.AGENT_MANAGE) ? (
            <Button size="sm" variant="ghost" onClick={() => setDeleting(agent)}>
              Delete
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <Page
      title="Agents"
      subtitle="People on the platform's roster."
      actions={
        can(PERMISSIONS.AGENT_MANAGE) ? (
          <Link to="/agents/new">
            <Button variant="primary">New agent</Button>
          </Link>
        ) : null
      }
    >
      <Card bodyless>
        <div className="card__body">
          <div className="toolbar">
            <div className="toolbar__search">
              <Input
                placeholder="Search by name, email, employee ID or department"
                value={list.search}
                onChange={(event) => list.setSearch(event.target.value)}
                aria-label="Search agents"
              />
            </div>
            <Select
              value={list.filters.status}
              onChange={(event) => list.setFilter('status', event.target.value)}
              aria-label="Filter by status"
            >
              <option value="">Any status</option>
              <option value="pending">Pending</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </Select>
          </div>
        </div>

        <DataTable
          columns={columns}
          rows={list.rows}
          query={list.query}
          empty={{
            title: 'No agents yet',
            body: 'Add the first agent to the roster, or clear the filters if you expected to see one.',
          }}
        />
        <Pagination meta={list.meta} page={list.page} onPageChange={list.setPage} />
      </Card>

      <Dialog
        open={Boolean(deleting)}
        onClose={closeDelete}
        title={`Remove ${deleting?.name}?`}
        warning={`They leave the roster immediately. ${deleting?.employeeId} stays reserved and is never reissued, because their printed ID card is still in circulation.`}
        footer={
          <>
            <Button variant="ghost" onClick={closeDelete}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={remove}
              loading={isWorking}
              disabled={reason.trim().length < 3}
            >
              Remove agent
            </Button>
          </>
        }
      >
        <Field label="Reason" hint="Recorded in the audit log against your account.">
          <Textarea
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Why is this agent being removed?"
          />
        </Field>
      </Dialog>

      <Dialog
        open={Boolean(idCardAgent)}
        onClose={() => setIdCardAgent(null)}
        size="wide"
        title={`ID card — ${idCardAgent?.name || ''}`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setIdCardAgent(null)}>
              Close
            </Button>
            <Button variant="primary" onClick={() => window.print()}>
              Print
            </Button>
          </>
        }
      >
        <div className="idcard-print">
          <AgentIdCard agent={idCardAgent} />
        </div>
      </Dialog>
    </Page>
  );
}
